import i18n from "../i18n";
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Users, ShieldCheck, ShieldX, Search, UserPlus, Trash2, Clock, Crown } from "lucide-react";
import { toast } from "sonner";

function roleBadgeStyle(role) {
    const r = (role || "").toLowerCase();
    if (r === "owner") return "bg-primary/15 text-primary border-primary/40";
    if (r.includes("manager") || r.includes("supervisor")) return "bg-blue-500/15 text-blue-700 border-blue-500/40";
    if (r.includes("vet")) return "bg-emerald-500/15 text-emerald-700 border-emerald-500/40";
    return "bg-slate-500/15 text-slate-700 dark:text-slate-300";
}

function StatCard({ icon: Icon, label, value, tone = "primary" }) {
    const tones = {
        primary: "bg-primary/10 text-primary",
        emerald: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
        amber: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
        rose: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
    };
    return (
        <Card className="p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl grid place-items-center ${tones[tone]}`}>
                <Icon className="w-5 h-5" />
            </div>
            <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
                <div className="text-2xl font-bold leading-tight">{value}</div>
            </div>
        </Card>
    );
}

export default function AdminUsersPage() {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("all"); // all | pending | approved | owners

    const load = async () => {
        try {
            setLoading(true);
            const r = await api.get("/admin/users");
            setUsers(r.data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load users");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const kpis = useMemo(() => {
        const total = users.length;
        const owners = users.filter((u) => (u.role_name || "").toLowerCase() === "owner").length;
        const pending = users.filter((u) => u.approved === false).length;
        const approved = users.filter((u) => u.approved === true).length;
        return { total, owners, pending, approved };
    }, [users]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return users.filter((u) => {
            if (filter === "pending" && u.approved !== false) return false;
            if (filter === "approved" && u.approved !== true) return false;
            if (filter === "owners" && (u.role_name || "").toLowerCase() !== "owner") return false;
            if (!q) return true;
            return (u.email || "").toLowerCase().includes(q) ||
                   (u.name || "").toLowerCase().includes(q) ||
                   (u.role_name || "").toLowerCase().includes(q);
        });
    }, [users, query, filter]);

    const setApproval = async (u, approve) => {
        try {
            if (approve) await api.post(`/admin/users/${u.id}/approve`);
            else await api.post(`/admin/users/${u.id}/revoke`);
            toast.success(approve ? `Approved ${u.email}` : `Revoked ${u.email}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
    };

    const deleteUser = async (u) => {
        if (!confirm(`Delete account "${u.email}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            toast.success(`Deleted ${u.email}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-users-page">
            <div>
                <h1 className="text-3xl sm:text-4xl font-bold flex items-center gap-3">
                    <Crown className="w-8 h-8 text-primary" />
                    {i18n.t("batch4.e603caadf1f7")}
                </h1>
                <p className="text-muted-foreground mt-1 text-sm">
                    {i18n.t("batch4.e5fac98e866d")}
                </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={Users} label={i18n.t("batch4.a028fce203b4")} value={kpis.total} tone="primary" />
                <StatCard icon={ShieldCheck} label={i18n.t("batch4.41b81eb8db1b")} value={kpis.approved} tone="emerald" />
                <StatCard icon={Clock} label={i18n.t("batch4.7ba0a4f030d1")} value={kpis.pending} tone={kpis.pending > 0 ? "amber" : "primary"} />
                <StatCard icon={Crown} label={i18n.t("batch4.4eaec4bc0f1d")} value={kpis.owners} tone="primary" />
            </div>

            <Card className="p-4">
                <div className="flex flex-wrap gap-2 items-center">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={i18n.t("batch4.8d1a6d814e76")}
                            className="pl-8 h-10"
                            data-testid="admin-user-search"
                        />
                    </div>
                    {[
                        ["all", "All"],
                        ["pending", `Pending (${kpis.pending})`],
                        ["approved", "Approved"],
                        ["owners", "Owners"],
                    ].map(([k, l]) => (
                        <Button
                            key={k}
                            size="sm"
                            variant={filter === k ? "default" : "outline"}
                            onClick={() => setFilter(k)}
                            data-testid={`filter-${k}`}
                        >
                            {l}
                        </Button>
                    ))}
                </div>
            </Card>

            <Card>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b bg-muted/40 text-left">
                                <th className="p-3 font-semibold">{i18n.t("batch4.9f8a2389a20c")}</th>
                                <th className="p-3 font-semibold">{i18n.t("batch4.c3f104d13657")}</th>
                                <th className="p-3 font-semibold">{i18n.t("batch4.bae7d5be7082")}</th>
                                <th className="p-3 font-semibold">{i18n.t("batch4.2df48ad223be")}</th>
                                <th className="p-3 font-semibold">{i18n.t("batch4.ed486d652fb9")}</th>
                                <th className="p-3 font-semibold text-right">{i18n.t("batch4.c3cd636a585b")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{i18n.t("batch4.b6443c996d50")}</td></tr>
                            )}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">
                                    {query ? "No users match your search." : "No users to display."}
                                </td></tr>
                            )}
                            {!loading && filtered.map((u) => {
                                const isOwner = (u.role_name || "").toLowerCase() === "owner";
                                const isSelf = u.id === currentUser?.id;
                                return (
                                    <tr key={u.id} className="border-b last:border-0 hover:bg-muted/20" data-testid={`user-row-${u.id}`}>
                                        <td className="p-3">
                                            <div className="font-semibold flex items-center gap-2">
                                                {u.name || "(no name)"}
                                                {isSelf && <Badge variant="outline" className="text-[10px]">{i18n.t("batch4.905cb326c779")}</Badge>}
                                            </div>
                                            <div className="text-xs text-muted-foreground">{u.email}</div>
                                        </td>
                                        <td className="p-3">
                                            <Badge variant="outline" className={`text-[10px] ${roleBadgeStyle(u.role_name)}`}>
                                                {u.role_name || "Worker"}
                                            </Badge>
                                        </td>
                                        <td className="p-3">
                                            {isOwner ? (
                                                <Badge variant="outline" className="text-[10px] bg-primary/15 text-primary">
                                                    {i18n.t("batch4.b0b0baa36b8f")}
                                                </Badge>
                                            ) : u.approved ? (
                                                <div className="flex items-center gap-2">
                                                    <Switch
                                                        checked={true}
                                                        onCheckedChange={() => setApproval(u, false)}
                                                        data-testid={`toggle-approve-${u.id}`}
                                                    />
                                                    <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                                                        <ShieldCheck className="w-3 h-3" />{i18n.t("batch4.41b81eb8db1b")}
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2">
                                                    <Switch
                                                        checked={false}
                                                        onCheckedChange={() => setApproval(u, true)}
                                                        data-testid={`toggle-approve-${u.id}`}
                                                    />
                                                    <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />{i18n.t("batch4.96f608c16cef")}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-3 text-xs text-muted-foreground">
                                            {u.last_login ? new Date(u.last_login).toLocaleString() : "—"}
                                        </td>
                                        <td className="p-3 text-xs text-muted-foreground">
                                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                                        </td>
                                        <td className="p-3 text-right">
                                            <div className="flex gap-1 justify-end">
                                                {!isOwner && !isSelf && u.approved === false && (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => setApproval(u, true)}
                                                        data-testid={`approve-${u.id}`}
                                                    >
                                                        <ShieldCheck className="w-3.5 h-3.5 mr-1" />{i18n.t("batch4.7b2c7f146aba")}
                                                    </Button>
                                                )}
                                                {!isOwner && !isSelf && u.approved === true && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => setApproval(u, false)}
                                                        data-testid={`revoke-${u.id}`}
                                                    >
                                                        <ShieldX className="w-3.5 h-3.5 mr-1" />{i18n.t("batch4.0be720759ff0")}
                                                    </Button>
                                                )}
                                                {!isOwner && !isSelf && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => deleteUser(u)}
                                                        data-testid={`delete-${u.id}`}
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Card>

            {kpis.pending > 0 && (
                <Card className="p-4 border-amber-500/40 bg-amber-500/5" data-testid="pending-banner">
                    <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                        <div>
                            <div className="font-semibold text-amber-700 dark:text-amber-400">
                                {kpis.pending} {i18n.t("batch4.12dea96fec20")}{kpis.pending === 1 ? "" : "s"} {i18n.t("batch4.c16049cead4f")}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                                {i18n.t("batch4.0c7a6cdc9e50")}
                            </div>
                        </div>
                        <Button size="sm" onClick={() => setFilter("pending")} className="ml-auto">
                            <UserPlus className="w-4 h-4 mr-1" />{i18n.t("batch4.e29a79fe0c34")}
                        </Button>
                    </div>
                </Card>
            )}
        </div>
    );
}
